import { cn } from 'cn';
import { isValidElement, ReactNode } from 'react';

import { Icon, Spinner, Tooltip } from '@vestigas/ui';

type P = {
  isLoading?: boolean;
  label: string;
  previousValue: ReactNode;
  tooltip?: string;
  trend?: 'up' | 'down';
  value: ReactNode;
} & ComponentStyling;

export const KPI = ({
  className,
  isLoading,
  label,
  previousValue,
  style,
  tooltip,
  trend,
  value,
}: P) => {
  return (
    <div
      className={cn('relative flex flex-col items-center', className)}
      style={style}
    >
      {Boolean(tooltip) && (
        <div className="absolute top-1 right-1 z-20">
          <Tooltip content={tooltip}>
            <span className="inline-block -translate-y-1 pl-1">
              <Icon
                className="cursor-help text-tiga-action-default transition-colors duration-300 hover:text-tiga-action-lighter"
                metaphor="infoOutlined"
                size="sm"
              />
            </span>
          </Tooltip>
        </div>
      )}
      <span
        className={cn(
          'relative flex w-full max-w-full flex-wrap items-center justify-center gap-x-1 text-center text-2xl leading-8 text-tiga-text-default',
          {
            'overflow-hidden text-ellipsis whitespace-nowrap':
              !isValidElement(value), // truncate text, but not React elements
          },
        )}
      >
        {isLoading && (
          <div className="animate-loadingOverlay absolute -top-1 -right-1 -bottom-0.5 -left-1 z-10 flex items-center justify-center rounded-md">
            <Spinner size={20} initialDelay={500} />
          </div>
        )}
        {value ?? <span className="text-tiga-text-lightest">—</span>}
      </span>

      <span className="mb-0.5 line-clamp-2 flex w-full flex-1 items-center justify-center text-center text-sm leading-4 text-tiga-text-default">
        {label}
      </span>

      <div className="-mb-1 flex items-center gap-1">
        {Boolean(trend) && (
          <Icon metaphor={trend === 'up' ? 'trendUp' : 'trendDown'} size="sm" />
        )}
        {previousValue && (
          <span className="text-sm text-tiga-text-default">
            {previousValue}
          </span>
        )}
      </div>
    </div>
  );
};
